import { Bell, Menu, PanelLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useBreadcrumb } from '@/components/common/BreadcrumbContext';
import { TopBarUser } from '@/components/common/TopBarUser';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import useAuth from '@/hooks/useAuth';
import cn from '@/utils/cn';
import { ROUTES } from '@/utils/constants';

interface SalesTopBarProps {
  onToggleSidebar: () => void;
  collapsed: boolean;
  isDesktop: boolean;
}

export function SalesTopBar({ onToggleSidebar, collapsed, isDesktop }: SalesTopBarProps) {
  const { user } = useAuth();
  const location = useLocation();
  const { extra } = useBreadcrumb();

  return (
    <header className="sticky top-0 z-40 h-14 border-b border-border bg-card/95 backdrop-blur">
      <div className="flex h-full items-center gap-3 px-4">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={onToggleSidebar}
          aria-label={
            isDesktop ? (collapsed ? 'Expand sidebar' : 'Collapse sidebar') : 'Open navigation menu'
          }
          aria-expanded={isDesktop ? !collapsed : undefined}
          aria-haspopup={isDesktop ? undefined : 'dialog'}
        >
          {isDesktop ? <PanelLeft className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>

        {/* min-w-0: without it this flex child cannot shrink below the
            breadcrumb's min-content, which pushed the bell and the user menu
            (the only logout path) off-screen behind the shell's overflow-hidden. */}
        <div className="min-w-0 flex-1">
          <Breadcrumbs path={location.pathname} extra={extra} />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative shrink-0"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[min(20rem,calc(100vw-2rem))] p-0">
            <div className="border-b border-border px-3 py-2 text-sm font-bold">Notifications</div>
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              You&apos;re all caught up.
            </p>
          </PopoverContent>
        </Popover>

        <Badge
          variant="outline"
          className="hidden border-warning bg-warning/30 text-foreground sm:inline-flex"
        >
          DEV
        </Badge>

        {/* Fix 8: profile and logout as two visible buttons, shared by all
            four workspace shells - see components/common/TopBarUser. */}
        <TopBarUser profileRoute={ROUTES.SALES_RM_PROFILE} roleLabel={user?.jobRole} />
      </div>
    </header>
  );
}

interface Crumb {
  label: string;
  // null = not navigable (the current page, or an in-page selection).
  href: string | null;
}

// One crumb per path segment, each linking to its cumulative path so clicking
// an ancestor actually navigates there. A numeric segment (record id) shows the
// page-supplied `extra` label instead of the raw id.
function buildCrumbs(path: string, extra: string | null): Crumb[] {
  const segments = path
    .replace(/^\/sales-rm/, '')
    .split('/')
    .filter(Boolean);
  if (segments.length === 0) segments.push('home');

  const crumbs: Crumb[] = [];
  let acc = '/sales-rm';
  segments.forEach((segment) => {
    acc += `/${segment}`;
    const isId = /^\d+$/.test(segment);
    const label = isId
      ? (extra ?? `#${segment}`)
      : segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    crumbs.push({ label, href: acc });
  });
  const lastIsId = /^\d+$/.test(segments[segments.length - 1] ?? '');
  if (extra && !lastIsId) crumbs.push({ label: extra, href: null });

  // The trailing crumb is the current page - never a link.
  return crumbs.map((c, i) => (i === crumbs.length - 1 ? { ...c, href: null } : c));
}

function Breadcrumbs({ path, extra }: { path: string; extra: string | null }) {
  const crumbs = buildCrumbs(path, extra);
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 items-center gap-1.5 overflow-hidden text-xs"
    >
      <span className="shrink-0 font-semibold text-muted-foreground">CRM</span>
      {crumbs.map((crumb, i) => (
        <span
          key={`${crumb.label}-${crumb.href ?? 'current'}`}
          className={cn(
            'flex items-center gap-1.5',
            i === crumbs.length - 1 ? 'min-w-0' : 'hidden shrink-0 sm:flex',
          )}
        >
          <span className="shrink-0 text-muted-foreground">/</span>
          {crumb.href ? (
            <Link
              to={crumb.href}
              className="truncate rounded text-muted-foreground transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {crumb.label}
            </Link>
          ) : (
            <span
              className={cn(
                'truncate',
                i === crumbs.length - 1 ? 'font-bold text-foreground' : 'text-muted-foreground',
              )}
              aria-current={i === crumbs.length - 1 ? 'page' : undefined}
            >
              {crumb.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}
